import React, { useState, useEffect } from 'react';
import { Plus, Ruler, Calendar, MessageSquare, TrendingUp, Edit2, Trash2, Check, X } from 'lucide-react';
import { 
  Area, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import Layout from '../components/Layout';
import Markdown from '../components/Markdown';
import ConfirmationModal from '../components/ConfirmationModal';
import { getBodyStats, logBodyStats, updateBodyStats, deleteBodyStats, renameBodyStatField } from '../utils/api';
import { formatDisplayDate } from '../utils/date';

const BodyStats = () => {
  const [stats, setStats] = useState([]);
  const [newEntry, setNewEntry] = useState({ measurements: {}, units: {}, comment: '', date: new Date().toISOString().split('T')[0] });
  const [showAdd, setShowAdd] = useState(false);
  const [showNewFieldForm, setShowNewFieldForm] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldUnit, setNewFieldUnit] = useState('');
  
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  const [globalEditingField, setGlobalEditingField] = useState(null);
  const [globalEditData, setGlobalEditData] = useState({ label: '', unit: '' });

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await getBodyStats();
      const data = res.data;
      setStats(data);
      
      // Pre-populate newEntry with last used labels and units if no current measurements exist
      if (data.length > 0 && Object.keys(newEntry.measurements).length === 0) {
        const lastMeasurements = data[0].measurements;
        const lastUnits = data[0].units || {};
        const emptyMeasurements = {};
        const carriedUnits = {};
        Object.keys(lastMeasurements).forEach(key => {
          emptyMeasurements[key] = '';
          carriedUnits[key] = lastUnits[key] || '';
        });
        setNewEntry(prev => ({ ...prev, measurements: emptyMeasurements, units: carriedUnits }));
      } else if (data.length === 0 && Object.keys(newEntry.measurements).length === 0) {
        // Start with empty measurements for first-time users
        setNewEntry(prev => ({ ...prev, measurements: {}, units: {} }));
      }
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  const handleLog = async (e) => {
    e.preventDefault();
    try {
      await logBodyStats(newEntry);
      setShowAdd(false);
      // After saving, keep the current labels and units for the next entry but clear values
      const resetMeasurements = {};
      Object.keys(newEntry.measurements).forEach(key => {
        resetMeasurements[key] = '';
      });
      setNewEntry({ measurements: resetMeasurements, units: { ...newEntry.units }, comment: '', date: new Date().toISOString().split('T')[0] });
      fetchStats();
    } catch (error) {
      console.error('Failed to log stats', error);
    }
  };

  const updateMeasure = (label, value) => {
    setNewEntry({
      ...newEntry,
      measurements: { ...newEntry.measurements, [label]: value }
    });
  };

  const updateUnit = (label, unit) => {
    setNewEntry({
      ...newEntry,
      units: { ...newEntry.units, [label]: unit }
    });
  };

  const addField = () => {
    if (!newFieldLabel.trim()) return;
    const normalizedLabel = newFieldLabel.trim();
    if (newEntry.measurements[normalizedLabel]) return; // Avoid duplicates
    
    setNewEntry({
      ...newEntry,
      measurements: { ...newEntry.measurements, [normalizedLabel]: '' },
      units: { ...newEntry.units, [normalizedLabel]: newFieldUnit.trim() }
    });
    setNewFieldLabel('');
    setNewFieldUnit('');
    setShowNewFieldForm(false);
  };

  const removeField = (label) => {
    const nextMeasurements = { ...newEntry.measurements };
    const nextUnits = { ...newEntry.units };
    delete nextMeasurements[label];
    delete nextUnits[label];
    setNewEntry({
      ...newEntry,
      measurements: nextMeasurements,
      units: nextUnits
    });
  };

  const startEditing = (s) => {
    setEditingId(s.id);
    setEditData({ ...s });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleUpdate = async () => {
    try {
      await updateBodyStats(editingId, editData);
      setEditingId(null);
      setEditData(null);
      fetchStats();
    } catch (error) {
      console.error('Failed to update stats', error);
    }
  };

  const handleDelete = async (id) => {
    setIdToDelete(id);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteBodyStats(idToDelete);
      fetchStats();
    } catch (error) {
      console.error('Failed to delete stats', error);
    } finally {
      setShowConfirmDelete(false);
      setIdToDelete(null);
    }
  };

  const updateEditMeasure = (label, value) => {
    setEditData({
      ...editData,
      measurements: { ...editData.measurements, [label]: value }
    });
  };

  const updateEditUnit = (label, unit) => {
    setEditData({
      ...editData,
      units: { ...editData.units, [label]: unit }
    });
  };

  const addEditField = (label, unit = '') => {
    if (!label.trim()) return;
    const normalizedLabel = label.trim();
    if (editData.measurements[normalizedLabel]) return;
    setEditData({
      ...editData,
      measurements: { ...editData.measurements, [normalizedLabel]: '' },
      units: { ...editData.units || {}, [normalizedLabel]: unit }
    });
  };

  const removeEditField = (label) => {
    const nextMeasurements = { ...editData.measurements };
    const nextUnits = { ...editData.units };
    delete nextMeasurements[label];
    delete nextUnits[label];
    setEditData({
      ...editData,
      measurements: nextMeasurements,
      units: nextUnits
    });
  };

  const handleGlobalUpdate = async () => {
    if (!globalEditData.label.trim()) return;
    const normalizedOldLabel = globalEditingField;
    const normalizedNewLabel = globalEditData.label.trim();
    const normalizedUnit = globalEditData.unit.trim();
    
    try {
      await renameBodyStatField(normalizedOldLabel, normalizedNewLabel, normalizedUnit);
      
      // Update newEntry if it contains the renamed label
      if (newEntry.measurements[normalizedOldLabel] !== undefined) {
        const nextMeasurements = { ...newEntry.measurements };
        const nextUnits = { ...newEntry.units };
        const val = nextMeasurements[normalizedOldLabel];
        delete nextMeasurements[normalizedOldLabel];
        nextMeasurements[normalizedNewLabel] = val;
        
        delete nextUnits[normalizedOldLabel];
        nextUnits[normalizedNewLabel] = normalizedUnit;
        
        setNewEntry(prev => ({
          ...prev,
          measurements: nextMeasurements,
          units: nextUnits
        }));
      }

      setGlobalEditingField(null);
      fetchStats();
    } catch (error) {
      console.error('Failed to update field globally', error);
    }
  };

  // Prepare chart data (reverse to show chronological order)
  const chartData = [...stats].reverse().map(s => ({
    date: s.date,
    ...s.measurements,
    units: s.units || {}
  }));

  const availableLabels = Array.from(new Set(stats.flatMap(s => Object.keys(s.measurements))));
  
  // Also include current labels if they are not in history yet, to avoid chart jumping
  const allLabels = Array.from(new Set([...availableLabels, ...Object.keys(newEntry.measurements)]));

  const CHART_COLORS = ['#A3E635', '#3B82F6', '#F59E0B', '#10B981', '#EC4899', '#8B5CF6', '#F43F5E', '#06B6D4'];
  const getLabelColor = (label) => {
    const idx = allLabels.indexOf(label);
    return CHART_COLORS[idx % CHART_COLORS.length];
  };

  const getUnitForLabel = (label) => {
    // Try to find the unit for this label from the history or current entry
    const entryWithLabel = stats.find(s => s.measurements[label] !== undefined && s.units && s.units[label]);
    if (entryWithLabel) return entryWithLabel.units[label];
    return newEntry.units[label] || '';
  };

  return (
    <Layout title="BODY STATS" subtitle="Track your physical progress">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Log Form & History */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black italic tracking-tighter uppercase">Evolution</h2>
              <button 
                onClick={() => setShowAdd(!showAdd)}
                className="bg-[#A3E635] text-black px-6 py-2 rounded-full font-black text-xs hover:scale-105 transition-transform"
              >
                {showAdd ? 'CANCEL' : 'ADD LOG'}
              </button>
            </div>

            {showAdd && (
              <form onSubmit={handleLog} className="mb-8 p-6 bg-[#2C2C2E] rounded-2xl space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] text-slate-500 font-black mb-1 uppercase">Date</label>
                    <input 
                      type="date" 
                      value={newEntry.date} 
                      onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                      className="w-full bg-[#1C1C1E] border-none rounded-xl px-4 py-2 text-sm outline-none"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                   <label className="block text-[10px] text-slate-500 font-black mb-1 uppercase">Values</label>
                   <div className="flex flex-wrap gap-2">
                     {Object.keys(newEntry.measurements).map(label => (
                       <div key={label} className="flex-1 min-w-[140px] relative group">
                         <div className="flex justify-between items-end mb-0.5">
                           <label className="block text-[8px] text-slate-600 font-black uppercase tracking-widest">{label}</label>
                            <input 
                              type="text"
                              placeholder="Unit"
                              value={newEntry.units[label] || ''}
                              onChange={(e) => updateUnit(label, e.target.value)}
                              className="bg-transparent border-none text-[8px] text-[#A3E635] font-black text-right outline-none w-10"
                            />
                         </div>
                         <input 
                           type="number" 
                           placeholder={label}
                           value={newEntry.measurements[label] || ''}
                           onChange={(e) => updateMeasure(label, e.target.value)}
                           className="w-full bg-[#1C1C1E] border-none rounded-xl px-4 py-2 text-sm outline-none"
                         />
                         <button 
                            type="button"
                            onClick={() => removeField(label)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                         >
                            <Plus size={8} className="rotate-45" />
                         </button>
                       </div>
                     ))}
                   </div>

                   <div className="pt-2">
                     {!showNewFieldForm ? (
                       <button 
                         type="button"
                         onClick={() => setShowNewFieldForm(true)}
                         className="flex items-center gap-2 text-[10px] text-[#A3E635] font-black uppercase tracking-widest hover:opacity-80 transition-opacity"
                       >
                         <Plus size={12} />
                         Add custom field
                       </button>
                     ) : (
                       <div className="flex flex-wrap gap-2 animate-in slide-in-from-top-1 duration-200">
                         <input 
                           type="text"
                           placeholder="Label (e.g. Neck)"
                           value={newFieldLabel}
                           onChange={(e) => setNewFieldLabel(e.target.value)}
                           className="flex-1 min-w-[120px] bg-[#1C1C1E] border-none rounded-xl px-4 py-2 text-sm outline-none"
                           autoFocus
                         />
                         <input 
                           type="text"
                           placeholder="Unit (e.g. cm)"
                           value={newFieldUnit}
                           onChange={(e) => setNewFieldUnit(e.target.value)}
                           className="w-24 bg-[#1C1C1E] border-none rounded-xl px-4 py-2 text-sm outline-none"
                         />
                         <div className="flex gap-1 shrink-0">
                           <button 
                             type="button"
                             onClick={addField}
                             className="bg-[#A3E635] text-black px-4 py-2 rounded-xl text-[10px] font-black hover:scale-105 transition-transform"
                           >
                             ADD
                           </button>
                           <button 
                             type="button"
                             onClick={() => setShowNewFieldForm(false)}
                             className="bg-[#1C1C1E] text-slate-500 px-3 py-2 rounded-xl text-[10px] font-black hover:text-white transition-colors"
                           >
                             <X size={14} />
                           </button>
                         </div>
                       </div>
                     )}
                   </div>
                </div>

                <div>
                  <label className="block text-[10px] text-slate-500 font-black mb-1 uppercase">Comment</label>
                  <textarea 
                    value={newEntry.comment}
                    onChange={(e) => setNewEntry({...newEntry, comment: e.target.value})}
                    className="w-full bg-[#1C1C1E] border-none rounded-xl px-4 py-2 text-sm outline-none h-20"
                  />
                </div>

                <button type="submit" className="w-full bg-[#A3E635] text-black font-black py-3 rounded-xl">SAVE LOG</button>
              </form>
            )}

            {chartData.length > 0 ? (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <defs>
                      {CHART_COLORS.map((color, idx) => (
                        <linearGradient key={`grad-${idx}`} id={`colorArea-${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={color} stopOpacity={0}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                    <XAxis dataKey="date" stroke="#555" fontSize={10} tickMargin={10} />
                    <YAxis 
                      stroke="#555" 
                      fontSize={10} 
                      tickMargin={10} 
                      domain={['dataMin - 5', 'dataMax + 5']}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid #2C2C2E', borderRadius: '12px' }}
                      itemStyle={{ fontWeight: 'bold' }}
                      formatter={(value, name, props) => {
                        const unit = props.payload.units?.[name] || getUnitForLabel(name);
                        return [`${value} ${unit}`.trim(), name];
                      }}
                    />
                    {allLabels.map((label, idx) => (
                      <React.Fragment key={label}>
                        <Area
                          type="monotone"
                          dataKey={label}
                          stroke="none"
                          fill={`url(#colorArea-${idx % CHART_COLORS.length})`}
                          legendType="none"
                          tooltipType="none"
                          connectNulls
                        />
                        <Line 
                          type="monotone" 
                          dataKey={label} 
                          stroke={getLabelColor(label)} 
                          strokeWidth={3} 
                          dot={{ fill: '#121214', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, strokeWidth: 0 }}
                          connectNulls
                        />
                      </React.Fragment>
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center border-2 border-dashed border-[#2C2C2E] rounded-3xl text-slate-600">
                <TrendingUp size={48} className="mb-2 opacity-20" />
                <p className="font-bold uppercase text-[10px]">No data to visualize yet</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-black tracking-tighter uppercase italic">Entry log</h3>
            {stats.length > 0 ? stats.map((s, i) => (
              <div key={s.id || i} className="bg-[#1C1C1E] rounded-[24px] p-6 border border-[#2C2C2E] hover:border-slate-600 transition-all group">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div className="flex-1">
                    {editingId === s.id ? (
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar size={18} className="text-[#A3E635]" />
                        <input 
                          type="date" 
                          value={editData.date} 
                          onChange={(e) => setEditData({...editData, date: e.target.value})}
                          className="bg-[#121214] border border-[#2C2C2E] rounded-xl px-4 py-2 text-sm outline-none text-white"
                        />
                      </div>
                    ) : (
                      <h4 className="text-lg md:text-xl font-black tracking-tight text-slate-200 uppercase italic">
                        {Object.entries(s.measurements).map(([label, val], idx, arr) => (
                          <React.Fragment key={label}>
                            {label} - <span style={{ color: getLabelColor(label) }}>{val} {s.units?.[label] || ''}</span>
                            {idx < arr.length - 1 && " // "}
                          </React.Fragment>
                        ))}
                      </h4>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    {!editingId && (
                      <span className="text-xs font-black italic text-slate-500 bg-[#2C2C2E] px-3 py-1.5 rounded-full uppercase tracking-widest whitespace-nowrap">
                        {formatDisplayDate(s.date)}
                      </span>
                    )}
                    
                    <div className="flex gap-2">
                      {editingId === s.id ? (
                        <>
                          <button 
                            onClick={handleUpdate}
                            className="p-2 bg-[#A3E635] text-black rounded-full hover:scale-110 transition-transform"
                          >
                            <Check size={16} strokeWidth={3} />
                          </button>
                          <button 
                            onClick={cancelEditing}
                            className="p-2 bg-[#2C2C2E] text-slate-400 rounded-full hover:scale-110 transition-transform"
                          >
                            <X size={16} strokeWidth={3} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            onClick={() => startEditing(s)}
                            className="p-2 bg-[#2C2C2E] text-slate-400 rounded-full hover:text-[#A3E635] hover:bg-[#A3E6351A] transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            onClick={() => handleDelete(s.id)}
                            className="p-2 bg-[#2C2C2E] text-slate-400 rounded-full hover:text-red-500 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {editingId === s.id ? (
                  <div className="w-full space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(editData.measurements).map(([label, val]) => (
                        <div key={label} className="relative group min-w-[120px]">
                          <div className="flex justify-between items-end mb-0.5">
                            <label className="block text-[8px] text-slate-600 font-black">{label}</label>
                            <input 
                              type="text"
                              placeholder="Unit"
                              value={editData.units?.[label] || ''}
                              onChange={(e) => updateEditUnit(label, e.target.value)}
                              className="bg-transparent border-none text-[8px] text-[#A3E635] font-black text-right outline-none w-10"
                            />
                          </div>
                          <input 
                            type="number" 
                            value={val || ''}
                            onChange={(e) => updateEditMeasure(label, e.target.value)}
                            className="w-full bg-[#121214] border border-[#2C2C2E] rounded-xl px-4 py-2 text-sm outline-none text-white"
                          />
                          <button 
                            type="button"
                            onClick={() => removeEditField(label)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white p-1 rounded-full"
                          >
                            <Plus size={8} className="rotate-45" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input 
                        type="text"
                        placeholder="Add field label..."
                        id="edit-new-label"
                        className="flex-[2] bg-[#121214] border border-[#2C2C2E] rounded-xl px-4 py-2 text-sm outline-none"
                      />
                      <input 
                        type="text"
                        placeholder="Unit..."
                        id="edit-new-unit"
                        className="flex-1 bg-[#121214] border border-[#2C2C2E] rounded-xl px-4 py-2 text-sm outline-none"
                      />
                      <button 
                        type="button"
                        onClick={() => {
                          const labelInput = document.getElementById('edit-new-label');
                          const unitInput = document.getElementById('edit-new-unit');
                          if (labelInput.value) {
                            addEditField(labelInput.value, unitInput.value);
                            labelInput.value = '';
                            unitInput.value = '';
                          }
                        }}
                        className="bg-[#2C2C2E] text-slate-400 px-4 py-2 rounded-xl text-xs font-black hover:bg-[#A3E635] hover:text-black transition-colors"
                      >
                        ADD
                      </button>
                    </div>
                    <div className="mt-4">
                      <label className="block text-[10px] text-slate-500 font-black mb-1 uppercase">Comment</label>
                      <textarea 
                        value={editData.comment}
                        onChange={(e) => setEditData({...editData, comment: e.target.value})}
                        className="w-full bg-[#121214] border border-[#2C2C2E] rounded-xl px-4 py-2 text-sm outline-none text-white h-20"
                      />
                    </div>
                  </div>
                ) : (
                  s.comment && (
                    <div className="text-base text-slate-400 font-medium leading-relaxed">
                      <Markdown content={s.comment} />
                    </div>
                  )
                )}
              </div>
            )) : (
              <div className="py-12 flex flex-col items-center justify-center border-2 border-dashed border-[#2C2C2E] rounded-[32px] text-slate-600">
                <Calendar size={48} className="mb-2 opacity-20" />
                <p className="font-bold uppercase text-[10px]">No logs recorded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <div className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E]">
             <Ruler className="text-[#A3E635] mb-4" size={32} />
             <h3 className="text-xl font-black italic tracking-tighter uppercase mb-4">Measured Fields</h3>
             <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-6">
               Active Tracking Points
             </p>
             <div className="space-y-3">
               {Object.keys(newEntry.measurements).length > 0 ? (
                 Object.keys(newEntry.measurements).map(label => (
                   <div key={label} className="bg-[#121214] border border-[#2C2C2E] rounded-2xl overflow-hidden">
                     {globalEditingField === label ? (
                       <div className="p-3 space-y-2">
                         <input 
                           type="text"
                           value={globalEditData.label}
                           onChange={(e) => setGlobalEditData({ ...globalEditData, label: e.target.value })}
                           className="w-full bg-[#1C1C1E] border-none rounded-xl px-3 py-1.5 text-xs outline-none text-white font-black"
                           placeholder="Label"
                         />
                         <input 
                           type="text"
                           value={globalEditData.unit}
                           onChange={(e) => setGlobalEditData({ ...globalEditData, unit: e.target.value })}
                           className="w-full bg-[#1C1C1E] border-none rounded-xl px-3 py-1.5 text-xs outline-none text-[#A3E635] font-black"
                           placeholder="Unit"
                         />
                         <div className="flex gap-2">
                           <button 
                             onClick={handleGlobalUpdate}
                             className="flex-1 bg-[#A3E635] text-black py-1.5 rounded-xl text-[10px] font-black"
                           >
                             SAVE
                           </button>
                           <button 
                             onClick={() => setGlobalEditingField(null)}
                             className="flex-1 bg-[#2C2C2E] text-slate-400 py-1.5 rounded-xl text-[10px] font-black"
                           >
                             CANCEL
                           </button>
                         </div>
                       </div>
                     ) : (
                       <div 
                         onClick={() => {
                           setGlobalEditingField(label);
                           setGlobalEditData({ label, unit: newEntry.units[label] || '' });
                         }}
                         className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#A3E6351A] transition-colors group"
                       >
                         <div className="flex items-center gap-2">
                           <div className="w-1.5 h-1.5 rounded-full bg-[#A3E635]"></div>
                           <span className="text-xs font-black text-slate-300 group-hover:text-[#A3E635] transition-colors">{label}</span>
                         </div>
                         <span className="text-[10px] font-black text-[#A3E635]">{newEntry.units[label] || 'No Unit'}</span>
                       </div>
                     )}
                   </div>
                 ))
               ) : (
                 <p className="text-slate-600 text-[10px] italic font-bold">No tracking points defined yet.</p>
               )}
             </div>
          </div>
        </div>

      </div>

      <ConfirmationModal 
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        title="Delete Log Entry?"
        message="Are you sure you want to delete this measurement log? This action cannot be undone."
      />
    </Layout>
  );
};

export default BodyStats;
